import React, { useState } from "react";
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Clock,
  Eye,
  FileText,
  PhoneOff,
  ShieldCheck,
  Target,
  Truck
} from "lucide-react";
import ShipNowForm from "../ShipNow/ShipNow";
import "./Shippers.css";

const Shippers = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  return (
    <div className="shippers-page">
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>
              Tired of Chasing Trucks? <span>We Answer the Phone.</span>
            </h1>
            <p className="subhead">
              We're a Midwest freight partner—not just another broker who disappears.
              That means reliable capacity, real-time tracking, and a real person who
              picks up when you call.
            </p>
            <div className="hero-cta">
              <button
                type="button"
                className="btn btn-primary btn-large"
                onClick={() => setIsFormOpen(true)}
              >
                Get Capacity Now →
              </button>
              <a className="btn btn-outline-white btn-large" href="tel:+13175370657">
                Call Us Direct
              </a>
            </div>
            <div className="hero-proof">
              <CheckCircle2 />
              Quotes returned in under 30 minutes during business hours
            </div>
          </div>
          <div className="hero-card">
            <h3>Get a Quote</h3>
            <p className="card-sub">Tell us where it's going. We'll handle the rest.</p>
            <div className="lane-examples">
              <p>Popular Lanes</p>
              <div className="lanes">
                <span className="lane-tag">Indianapolis → Columbus</span>
                <span className="lane-tag">Louisville → Indianapolis</span>
                <span className="lane-tag">Indianapolis → Chicago</span>
                <span className="lane-tag">Champaign → Cincinnati</span>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-large"
          onClick={() => setIsFormOpen(true)}
        >
              Request Quote →
            </button>
            <div className="response-time">
              <Clock />
              Avg. response: 22 minutes
            </div>
          </div>
        </div>
      </section>

      <section className="pain-section">
        <div className="pain-content">
          <div className="pain-header">
            <h2>Sound Familiar?</h2>
            <p>These are the problems we hear every day. Here's how we solve them.</p>
          </div>
          <div className="pain-grid">
            <div className="pain-card">
              <div className="pain-icon">
                <PhoneOff />
              </div>
              <div>
                <h4>"My broker never answers"</h4>
                <p>You get a dedicated rep who actually picks up. 24/7 support for active loads.</p>
              </div>
            </div>
            <div className="pain-card">
              <div className="pain-icon">
                <AlertCircle />
              </div>
              <div>
                <h4>"Trucks keep falling off my loads"</h4>
                <p>We build your routing guide and monitor carrier performance to keep service steady.</p>
              </div>
            </div>
            <div className="pain-card">
              <div className="pain-icon">
                <Clock />
              </div>
              <div>
                <h4>"I can't get capacity when I need it"</h4>
                <p>Plug into our network for capacity, even when the market's tight.</p>
              </div>
            </div>
            <div className="pain-card">
              <div className="pain-icon">
                <Eye />
              </div>
              <div>
                <h4>"Where's my freight right now?"</h4>
                <p>Real-time GPS tracking on every load. Proactive updates before you have to ask.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="asset-section">
        <div className="asset-content">
          <div className="asset-text">
            <h2>
              Reliable Capacity. <span>That Changes Everything.</span>
            </h2>
            <p>
              We’re not just another broker. We build lasting carrier relationships first,
              which means you get priority access to trusted capacity with professional drivers.
            </p>
            <ul className="asset-benefits">
              <li>
                <CheckCircle2 />
                Vetted capacity across your lanes
              </li>
              <li>
                <CheckCircle2 />
                Drivers who understand your facilities and requirements
              </li>
              <li>
                <CheckCircle2 />
                Clear pricing with proactive updates
              </li>
              <li>
                <CheckCircle2 />
                Vetted brokerage network for overflow and specialized freight
              </li>
            </ul>
          </div>
          <div className="asset-stats">
            <div className="asset-stat">
              <div className="asset-stat-number">98.5%</div>
              <div className="asset-stat-label">On-Time Delivery</div>
            </div>
            <div className="asset-stat">
              <div className="asset-stat-number">0.3%</div>
              <div className="asset-stat-label">Claims Ratio</div>
            </div>
            <div className="asset-stat">
              <div className="asset-stat-number">22 min</div>
              <div className="asset-stat-label">Avg. Quote Response</div>
            </div>
            <div className="asset-stat">
              <div className="asset-stat-number">24/7</div>
              <div className="asset-stat-label">Support for Active Loads</div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-gray">
        <div className="section-header">
          <h2>Technology That Actually Helps</h2>
          <p>Powered by tech. Built for visibility, not vanity metrics.</p>
        </div>
        <div className="tech-grid">
          <div className="tech-card">
            <Target className="tech-card-icon" />
            <h3>Integrated Live Tracking</h3>
            <p>We use integrated tracking tech to keep you updated without a separate dashboard.</p>
          </div>
          <div className="tech-card">
            <FileText className="tech-card-icon" />
            <h3>Digital Documents</h3>
            <p>BOLs, PODs, and invoices in one place. Download anytime, no chasing paperwork.</p>
          </div>
          <div className="tech-card">
            <Bell className="tech-card-icon" />
            <h3>Proactive Alerts</h3>
            <p>Get notified on pickups, delays, and deliveries before you have to ask.</p>
          </div>
        </div>
      </section>

      <section className="testimonials-section">
        <div className="testimonials-header">
          <h2>Capacity You Can Measure</h2>
          <p>Lane history, carrier scorecards, and live tracking guide every dispatch.</p>
        </div>
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <h4>Scorecards That Protect Service</h4>
            <p className="testimonial-quote">
              We track on-time, claims, and response times so your freight stays with top performers.
            </p>
          </div>
          <div className="testimonial-card">
            <h4>Routing Guides Built on Data</h4>
            <p className="testimonial-quote">
              Lane history guides every dispatch, keeping decisions consistent and repeatable.
            </p>
          </div>
        </div>
      </section>

      <section className="cta-section" id="quote">
        <div className="cta-card">
          <h2>Ready to Ship Without the Headaches?</h2>
          <p>Get a quote in under 30 minutes. Talk to a real person who knows freight.</p>
          <div className="cta-buttons">
            <button
              type="button"
              className="btn btn-primary btn-large"
              onClick={() => setIsFormOpen(true)}
            >
              Get Capacity Now →
            </button>
            <a className="btn btn-outline-white btn-large" href="tel:+13175370657">
              Call (317) 537-0657
            </a>
          </div>
          <p className="cta-contact">
            Or email us at <a href="mailto:ship@sunnylogistics.co">ship@sunnylogistics.co</a>
          </p>
        </div>
      </section>

      <footer className="shippers-footer">
        <div className="footer-content">
          <div className="footer-col">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="/">Home</a></li>
              <li><a href="/shippers">Ship with us</a></li>
              <li><a href="/carriers">Register as a Carrier</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Our Office</h4>
            <address>
              11 MUNICIPAL DRIVE<br />
              SUITE 200<br />
              FISHERS, IN 46038
            </address>
          </div>
          <div className="footer-col">
            <h4>Legal</h4>
            <ul>
              <li><a href="/terms">Terms of Service</a></li>
              <li><a href="/privacy">Privacy Policy</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Sunny Logistics. All rights reserved.</p>
        </div>
      </footer>

      <ShipNowForm open={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </div>
  );
};

export default Shippers;
